import { useEffect, useRef } from 'react';
import { Box, FlexBox, Icon, IconButton, Typography } from '@vp/swan';
import type { CSSProperties, ReactNode } from 'react';

export interface WizardStepDefinition {
    title: ReactNode;
    /**
     * The body of this step. For a step that asks the user to pick one of
     * several icon+label options, use `WizardOptionList` here rather than
     * hand-building a `SelectionSet` — see `./WizardOptionList`.
     */
    content: ReactNode;
    footer?: ReactNode;
    /** Disables the "next" chevron while this step is active. Default: false. */
    nextDisabled?: boolean;
    /** Disables the "previous" chevron while this step is active. Default: false. */
    previousDisabled?: boolean;
    /**
     * Set to false while this step's content is still loading async data.
     * Defers heading focus until it flips back to true. Default: true.
     */
    isReady?: boolean;
}

export interface WizardStepProps {
    steps: readonly WizardStepDefinition[];
    activeStep: number;
    onStepChange: (step: number) => void;
    previousStepLabel: string;
    nextStepLabel: string;
    className?: string;
    style?: CSSProperties;
}

export function WizardStep({
    steps,
    activeStep,
    onStepChange,
    previousStepLabel,
    nextStepLabel,
    className,
    style,
}: WizardStepProps) {
    const headingRef = useRef<HTMLHeadingElement>(null);
    const currentStep = getValidStep(activeStep, steps.length);
    const step = steps[currentStep - 1];
    const hasNavigation = steps.length > 1;
    const isStepReady = step?.isReady !== false;

    useEffect(() => {
        if (currentStep === 0 || !isStepReady) return;

        const frame = requestAnimationFrame(() => {
            headingRef.current?.focus();
        });

        return () => cancelAnimationFrame(frame);
    }, [currentStep, isStepReady]);

    if (!step) return null;

    const isFirstStep = currentStep === 1;
    const isLastStep = currentStep === steps.length;

    return (
        <Box
            className={className}
            style={{
                backgroundColor: 'var(--swan-sem-color-bg-active)',
                border: '1px solid var(--swan-sem-color-border-standard)',
                padding: 'var(--swan-sem-space-5)',
                borderRadius:
                    'var(--swan-sem-border-radius-strong) var(--swan-sem-border-radius-strong) 0 0',
                ...style,
            }}
        >
            <FlexBox
                justifyContent="space-between"
                alignItems="center"
                style={{ minHeight: '44px', marginBottom: 'var(--swan-sem-space-5)' }}
            >
                <Typography
                    ref={headingRef}
                    as="h2"
                    tabIndex={-1}
                    fontSkin="title-item"
                    style={{ outline: 'none', boxShadow: 'none' }}
                >
                    {step.title}
                </Typography>
                {hasNavigation && (
                    <FlexBox alignItems="center" gap={2}>
                        <IconButton
                            skin="tertiary"
                            onClick={() => onStepChange(currentStep - 1)}
                            disabled={isFirstStep || step.previousDisabled}
                            aria-label={previousStepLabel}
                            tooltip={previousStepLabel}
                        >
                            <Icon iconType="chevronLeft" size="standard" />
                        </IconButton>
                        <Typography
                            fontSkin="body-small-bold"
                            display={{ xs: 'none', sm: 'block' }}
                        >
                            {currentStep} of {steps.length}
                        </Typography>
                        <IconButton
                            skin="tertiary"
                            onClick={() => onStepChange(currentStep + 1)}
                            disabled={isLastStep || step.nextDisabled}
                            aria-label={nextStepLabel}
                            tooltip={nextStepLabel}
                        >
                            <Icon iconType="chevronRight" size="standard" />
                        </IconButton>
                    </FlexBox>
                )}
            </FlexBox>

            {step.content}
            {step.footer && <Box mt={5}>{step.footer}</Box>}
        </Box>
    );
}

function getValidStep(step: number, totalSteps: number): number {
    if (totalSteps === 0) return 0;
    return Math.min(Math.max(step, 1), totalSteps);
}
